import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AdminChrome from '@/components/AdminChrome';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Folder, FileText, LayoutTemplate, Upload, Palette, ShieldCheck, Lock,
  ArrowLeft, Plus, Loader2, Trash2, Download, Edit2, Eye, EyeOff, FolderOpen, Users, X,
} from 'lucide-react';

const ICON_MAP = {
  Folder, FileText, LayoutTemplate, Upload, Palette, ShieldCheck, Lock,
};

const INTERN_ROLES = [
  { key: 'social_media', label: 'Social Media' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'content_curriculum', label: 'Content / Curriculum' },
  { key: 'support', label: 'Support' },
  { key: 'sales', label: 'Sales' },
  { key: 'hr', label: 'HR' },
];

const bytesFmt = (n) => {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0, x = n;
  while (x >= 1024 && i < units.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const ResourcesPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [openFolder, setOpenFolder] = useState(null); // {folder, files, can_upload}
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [folderDialog, setFolderDialog] = useState({ open: false, editing: null });
  const fileInputRef = useRef(null);

  const loadFolders = async () => {
    setLoading(true);
    try {
      const res = await api.get('/resources/folders');
      setFolders(res.data.folders || []);
      setIsAdmin(!!res.data.is_admin);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const openFolderDetail = async (folder) => {
    try {
      const res = await api.get(`/resources/folders/${folder.id}/files`);
      setOpenFolder({
        folder: res.data.folder,
        files: res.data.files || [],
        can_upload: res.data.can_upload,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open folder');
    }
  };

  useEffect(() => { loadFolders(); /* eslint-disable-next-line */ }, []);

  // ==================== Upload ====================
  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !openFolder) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/resources/folders/${openFolder.folder.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${file.name}`);
      await openFolderDetail(openFolder.folder);
      loadFolders(); // refresh file counts
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (fileId) => {
    if (!openFolder) return;
    if (!window.confirm('Delete this file? This cannot be undone.')) return;
    try {
      await api.delete(`/resources/folders/${openFolder.folder.id}/files/${fileId}`);
      toast.success('File deleted');
      await openFolderDetail(openFolder.folder);
      loadFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // Auth-aware download — we need to fetch with the token then trigger a save
  const handleDownload = async (file) => {
    try {
      const res = await api.get(`/files/${file.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename || file.id;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  // ==================== Admin folder CRUD ====================
  const openCreate = () => setFolderDialog({
    open: true,
    editing: {
      name: '',
      description: '',
      icon: 'Folder',
      employees_can_upload: true,
      visible_to_roles: [],
      _isNew: true,
    },
  });

  const openEdit = (folder) => setFolderDialog({
    open: true,
    editing: {
      id: folder.id,
      name: folder.name,
      description: folder.description || '',
      icon: folder.icon || 'Folder',
      employees_can_upload: !!folder.employees_can_upload,
      visible_to_roles: folder.visible_to_roles || [],
      is_system: !!folder.is_system,
      _isNew: false,
    },
  });

  const saveFolder = async () => {
    const f = folderDialog.editing;
    if (!f?.name?.trim()) { toast.error('Name is required'); return; }
    try {
      const body = {
        name: f.name.trim(),
        description: f.description || '',
        icon: f.icon || 'Folder',
        employees_can_upload: !!f.employees_can_upload,
        visible_to_roles: f.visible_to_roles || [],
      };
      if (f._isNew) {
        await api.post('/resources/folders', body);
        toast.success('Folder created');
      } else {
        await api.patch(`/resources/folders/${f.id}`, body);
        toast.success('Folder updated');
      }
      setFolderDialog({ open: false, editing: null });
      loadFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"?`)) return;
    try {
      await api.delete(`/resources/folders/${folder.id}`);
      toast.success('Folder deleted');
      loadFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleRole = (role) => {
    setFolderDialog((d) => {
      const roles = d.editing.visible_to_roles.includes(role)
        ? d.editing.visible_to_roles.filter((r) => r !== role)
        : [...d.editing.visible_to_roles, role];
      return { ...d, editing: { ...d.editing, visible_to_roles: roles } };
    });
  };

  // ==================== Render ====================
  if (loading) {
    return (
      <AdminChrome activeSection="resources">
        <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      </AdminChrome>
    );
  }

  // Detail view (files inside a folder)
  if (openFolder) {
    const FolderIcon = ICON_MAP[openFolder.folder.icon] || Folder;
    return (
      <AdminChrome activeSection="resources">
        <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
          <div>
            <Button variant="ghost" size="sm" onClick={() => setOpenFolder(null)} data-testid="back-to-folders">
              <ArrowLeft className="w-4 h-4 mr-1" /> All folders
            </Button>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-3">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <FolderIcon className="w-6 h-6 text-emerald-500" /> {openFolder.folder.name}
                </h1>
                {openFolder.folder.description && (
                  <p className="text-sm text-slate-500 mt-1">{openFolder.folder.description}</p>
                )}
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline" className={openFolder.can_upload ? 'text-emerald-700 border-emerald-300 bg-emerald-50' : 'text-slate-600 border-slate-300 bg-slate-50'}>
                    {openFolder.can_upload ? <><Upload className="w-3 h-3 mr-1" /> Uploads open</> : <><EyeOff className="w-3 h-3 mr-1" /> View-only</>}
                  </Badge>
                  {openFolder.folder.visible_to_roles?.length > 0 && (
                    <Badge variant="outline" className="text-indigo-700 border-indigo-300 bg-indigo-50">
                      <Users className="w-3 h-3 mr-1" /> Role-gated
                    </Badge>
                  )}
                </div>
              </div>
              {openFolder.can_upload && (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileChosen}
                    data-testid="folder-upload-input"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="folder-upload-btn"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                    Upload file
                  </Button>
                </div>
              )}
            </div>
          </div>

          {openFolder.files.length === 0 ? (
            <Card>
              <CardContent className="p-10 text-center">
                <FolderOpen className="w-12 h-12 mx-auto text-slate-300" />
                <p className="text-slate-500 mt-2">No files yet.</p>
                {openFolder.can_upload && <p className="text-slate-400 text-sm mt-1">Click &quot;Upload file&quot; to add the first one.</p>}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2" data-testid="folder-files-list">
              {openFolder.files.map((f) => (
                <Card key={f.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate" data-testid={`file-name-${f.id}`}>{f.original_filename || 'Untitled'}</p>
                      <p className="text-xs text-slate-500">
                        {bytesFmt(f.size)} · Uploaded by {f.uploaded_by_name} · {new Date(f.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleDownload(f)} data-testid={`file-download-${f.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      {(f.user_id === (user?.id || user?.user_id) || isAdmin) && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(f.id)} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`file-delete-${f.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </AdminChrome>
    );
  }

  // Grid view (all folders)
  return (
    <AdminChrome activeSection="resources">
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="resources-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 mt-2 flex items-center gap-2" data-testid="resources-title">
              <FolderOpen className="w-6 h-6 text-emerald-500" /> Resources
            </h1>
            <p className="text-sm text-slate-500 mt-1">Shared team files, templates, brand assets, and reference documents.</p>
          </div>
          {isAdmin && (
            <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-folder-btn">
              <Plus className="w-4 h-4 mr-1" /> New folder
            </Button>
          )}
        </div>

        {folders.length === 0 ? (
          <Card>
            <CardContent className="p-10 text-center text-slate-500">No folders yet.</CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="folders-grid">
            {folders.map((f) => {
              const FolderIcon = ICON_MAP[f.icon] || Folder;
              return (
                <Card
                  key={f.id}
                  className="hover:shadow-lg hover:border-emerald-300 transition-all cursor-pointer group"
                  onClick={() => openFolderDetail(f)}
                  data-testid={`folder-card-${f.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center flex-shrink-0">
                        <FolderIcon className="w-6 h-6 text-emerald-600" />
                      </div>
                      {isAdmin && (
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(f); }} data-testid={`folder-edit-${f.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {!f.is_system && (
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); deleteFolder(f); }} className="text-rose-600" data-testid={`folder-delete-${f.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    <h3 className="mt-3 font-semibold text-slate-900" data-testid={`folder-name-${f.id}`}>{f.name}</h3>
                    {f.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{f.description}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      <Badge variant="outline" className="text-xs">{f.file_count} file{f.file_count === 1 ? '' : 's'}</Badge>
                      <Badge variant="outline" className={f.can_upload ? 'text-xs text-emerald-700 border-emerald-300 bg-emerald-50' : 'text-xs text-slate-600'}>
                        {f.can_upload ? <><Upload className="w-3 h-3 mr-1" /> Upload</> : <><Eye className="w-3 h-3 mr-1" /> View-only</>}
                      </Badge>
                      {f.is_system && <Badge variant="outline" className="text-xs text-indigo-600">System</Badge>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Admin: create/edit folder dialog */}
      <Dialog open={folderDialog.open} onOpenChange={(o) => (o ? null : setFolderDialog({ open: false, editing: null }))}>
        <DialogContent data-testid="folder-dialog">
          <DialogHeader>
            <DialogTitle>{folderDialog.editing?._isNew ? 'New folder' : 'Edit folder'}</DialogTitle>
            <DialogDescription>
              {folderDialog.editing?._isNew
                ? 'Create a shared folder for the team.'
                : 'Update visibility, upload permissions, or rename.'}
            </DialogDescription>
          </DialogHeader>
          {folderDialog.editing && (
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={folderDialog.editing.name}
                  onChange={(e) => setFolderDialog((d) => ({ ...d, editing: { ...d.editing, name: e.target.value } }))}
                  data-testid="folder-name-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  rows={2}
                  value={folderDialog.editing.description}
                  onChange={(e) => setFolderDialog((d) => ({ ...d, editing: { ...d.editing, description: e.target.value } }))}
                  data-testid="folder-desc-input"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={folderDialog.editing.employees_can_upload}
                  onChange={(e) => setFolderDialog((d) => ({ ...d, editing: { ...d.editing, employees_can_upload: e.target.checked } }))}
                  data-testid="folder-can-upload-checkbox"
                />
                <span>Employees/interns can upload (unchecked = view-only)</span>
              </label>
              <div>
                <Label>Visible to roles (optional — empty = everyone)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {INTERN_ROLES.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => toggleRole(r.key)}
                      className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                        folderDialog.editing.visible_to_roles.includes(r.key)
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-400'
                      }`}
                      data-testid={`folder-role-${r.key}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">Admins always see every folder regardless of role gating.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderDialog({ open: false, editing: null })}>Cancel</Button>
            <Button onClick={saveFolder} className="bg-emerald-600 hover:bg-emerald-700" data-testid="folder-save-btn">
              {folderDialog.editing?._isNew ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminChrome>
  );
};

export default ResourcesPage;
